import { SystemStyleObject, theme } from '@chakra-ui/react';
import type { ComponentStyleConfig } from '@chakra-ui/theme';
import { StyleFunctionProps } from '@chakra-ui/theme-tools';

const getVisuallyDisabledStyles = (
  disabledStyles: SystemStyleObject,
  hoverDisabledStyles?: SystemStyleObject
): SystemStyleObject => ({
  '&[data-visually-disabled]': {
    ...disabledStyles,
    cursor: 'pointer',
    _active: hoverDisabledStyles ?? disabledStyles,
    _hover: hoverDisabledStyles ?? disabledStyles,
  },
});

const primary = (props: StyleFunctionProps) => {
  const disabledStyles: SystemStyleObject = {
    bg: 'neutral.fill-subtle',
    color: 'neutral.fg-disabled',
    opacity: 1,
  };

  return {
    ...theme.components.Button.variants?.solid(props),
    bg: 'brand.solid-primary',
    color: 'misc.white-fix-primary',
    borderColor: 'transparent',
    _hover: {
      bg: ['brand.solid-primary', 'brand.solid-hover'],
      _disabled: disabledStyles,
    },
    _active: {
      bg: 'brand.solid-hover',
    },
    _disabled: disabledStyles,
    ...getVisuallyDisabledStyles(disabledStyles),
  };
};

const secondary = (props: StyleFunctionProps) => {
  const disabledStyles: SystemStyleObject = {
    bg: 'neutral.bg-primary',
    color: 'neutral.fg-disabled',
    opacity: 1,
  };
  const hoverDisabledStyles: SystemStyleObject = {
    bg: 'neutral.bg-primary',
    color: 'neutral.fg-disabled',
    borderColor: 'neutral.border-base',
  };

  return {
    ...theme.components.Button.variants?.outline(props),
    bg: 'neutral.bg-primary',
    color: 'neutral.fg-primary',
    borderColor: 'neutral.border-base',
    _hover: {
      color: 'brand.fg-primary',
      borderColor: 'brand.border-base',
      _disabled: hoverDisabledStyles,
    },
    _active: {
      bg: 'neutral.bg-tertiary',
      color: 'brand.fg-primary',
      borderColor: 'brand.border-base',
    },
    _disabled: disabledStyles,
    ...getVisuallyDisabledStyles(disabledStyles, hoverDisabledStyles),
  };
};

const tertiary = (props: StyleFunctionProps) => {
  const disabledStyles: SystemStyleObject = {
    color: 'neutral.fg-disabled',
    opacity: 1,
  };

  return {
    ...theme.components.Button.variants?.outline(props),
    px: '5',
    py: '2',
    border: '0',
    minW: 'auto',
    color: 'brand.fg-primary',
    textDecoration: 'underline',
    textUnderlineOffset: 3,
    _hover: {
      color: 'brand.fg-secondary',
      _disabled: disabledStyles,
    },
    _active: {
      color: 'brand.fg-primary',
    },
    _disabled: disabledStyles,
    ...getVisuallyDisabledStyles(disabledStyles),
  };
};

const negative = (props: StyleFunctionProps) => {
  const disabledStyles: SystemStyleObject = {
    bg: 'neutral.fill-subtle',
    borderColor: 'transparent',
    color: 'neutral.fg-disabled',
    opacity: 1,
  };
  const hoverDisabledStyles: SystemStyleObject = {
    bg: 'neutral.fill-subtle',
    borderColor: 'transparent',
  };

  return {
    ...theme.components.Button.variants?.solid(props),
    bg: 'negative.solid-primary',
    borderColor: 'negative.solid-primary',
    color: 'misc.white-fix-primary',
    _hover: {
      bg: 'negative.solid-hover',
      borderColor: 'negative.solid-hover',
      _disabled: hoverDisabledStyles,
    },
    _active: {
      bg: 'negative.fg-secondary',
      borderColor: 'negative.fg-secondary',
    },
    _disabled: disabledStyles,
    ...getVisuallyDisabledStyles(disabledStyles, hoverDisabledStyles),
  };
};

const unstyled = (_props: StyleFunctionProps) => ({
  ...theme.components.Button.variants?.unstyled,
  borderStyle: 'none',
});

const link = () => ({
  ...theme.components.Button.variants?.link,
  borderStyle: 'none',
});

const ghost = () => ({
  ...theme.components.Button.variants?.ghost,
  borderStyle: 'none',
});

export const styleConfig: ComponentStyleConfig = {
  baseStyle: {
    fontWeight: 'bold',
    height: 'auto',
    minW: '40',
    border: '0.1',
    borderRadius: 'lg',
    _focusVisible: {
      outline: 0,
      boxShadow: '0 0 0 1px white, 0 0 0 3px #6691FF',
    },
  },
  sizes: {
    default: {
      fontSize: 'lg',
      fontWeight: 'semibold',
      lineHeight: 'short',
      height: '12',
      py: '2.5',
      px: '6',
    },
    large: {
      fontSize: 'xl',
      lineHeight: 'tiny',
      height: '16',
      py: '4',
      px: '8',
      borderRadius: 'xl',
    },
    small: {
      py: '2',
      px: '5',
      height: '9',
      fontSize: 'md',
      lineHeight: 'none',
    },
  },
  variants: {
    primary,
    secondary,
    tertiary,
    negative,
    unstyled,
    link,
    ghost,
  },
  defaultProps: {
    variant: 'primary',
    size: 'default',
  },
};
